import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { BadgeModule } from 'primeng/badge';
import { TagModule } from 'primeng/tag';
import { Toast } from '@/shared/components/toast/toast';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CardSortPills, CardSortOption } from '@/shared/components/layout/card-sort-pills/card-sort-pills';
import { TipoDebitoService } from '@/core/api/sicom-api/financeiro/arrecadacao/tipo-debito/tipo-debito.service';
import { TipoDebitoManutencaoResponse } from '@/core/api/sicom-api/financeiro/arrecadacao/tipo-debito/tipo-debito.model';
import { DashboardPerfilService } from '@/features/portal/dashboard-profile/dashboard-profile.service';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { LayoutService } from '@/layout/service/layout.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-anuidades',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        TableModule,
        TooltipModule,
        InputTextModule,
        BadgeModule,
        TagModule,
        Toast,
        PaginatorModule,
        SelectModule,
        ToggleSwitchModule,
        CardContainer,
        TableViewSwitcherComponent,
        CardContent,
        SelectEstado,
        CanDirective,
        FiltrosBar,
        FiltroCampo,
        CardSortPills
    ],
    providers: [MessageService],
    templateUrl: './anuidades.html',
    styleUrl: './anuidades.scss'
})
/** Configuração de anuidades: lista/grade (filtro só no "Filtrar") + modal de detalhe. */
export class Anuidades implements OnInit {
    private readonly tipoDebitoService = inject(TipoDebitoService);
    private readonly messageService = inject(MessageService);
    protected readonly roleAnuidadesEditar = PERMISSOES.configuracoes.anuidades.editar;
    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private readonly perfilService = inject(DashboardPerfilService);
    private readonly layoutService = inject(LayoutService);

    viewMode = signal<'table' | 'card'>(this.layoutService.getPresentationType());
    filtro = signal('');
    carregando = signal(false);
    items = signal<TipoDebitoManutencaoResponse[]>([]);
    totalRecords = signal(0);
    pageSize = signal(20);
    currentPage = signal(0);
    sortField = signal('id_tpdebito');
    sortOrder = signal<1 | -1>(1);

    /** Token incremental para descartar respostas fora de ordem (filtro/paginação rápidos). */
    private requisicaoAtual = 0;

    /** Totais por tipo dos registros exibidos na página atual (card de resumo). */
    resumoPagina = computed(() => {
        const lista = this.items();
        return {
            anuidades: lista.filter((i) => i.in_anuidade === 'S').length,
            multas: lista.filter((i) => i.in_anuidade === 'M').length,
            taxas: lista.filter((i) => i.in_anuidade !== 'S' && i.in_anuidade !== 'M').length
        };
    });

    /** Faixa de registros exibida, ex.: "21–40". */
    exibindoLabel = computed(() => {
        const total = this.totalRecords();
        const qtd = this.items().length;
        if (!total || !qtd) return '0';
        const inicio = this.currentPage() * this.pageSize() + 1;
        return `${inicio}–${Math.min(total, inicio + qtd - 1)}`;
    });

    ufUsuario = signal<string>('');
    ufFiltro = signal<string>('');
    filtrarPorUf = signal(true);
    filtroTipo = signal<string>('');
    isUsuarioBR = computed(() => this.ufUsuario() === 'BR');
    mostrarSelectEstado = computed(() => this.isUsuarioBR() && this.filtrarPorUf());

    protected readonly tipoOptions = [
        { label: 'Todos', value: '' },
        { label: 'Anuidade', value: 'S' },
        { label: 'Taxa', value: 'N' },
        { label: 'Multa Eleitoral', value: 'M' }
    ];

    /**
     * Critérios das pills do modo card. Um por campo: a direção vem do clique na pill,
     * não de opções "(A→Z)/(Z→A)" separadas como no seletor que havia aqui.
     */
    protected readonly sortOpcoes: CardSortOption[] = [
        { field: 'id_tpdebito', label: 'Código' },
        { field: 'ds_debito', label: 'Descrição' },
        { field: 'in_anuidade', label: 'Tipo' }
    ];

    ngOnInit(): void {
        this.perfilService
            .buscarPerfil()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    const perfil = Array.isArray(res) ? res[0] : res;
                    const uf = (perfil?.uf ?? '').trim().toUpperCase();
                    this.ufUsuario.set(uf);
                    this.ufFiltro.set(uf === 'BR' ? 'AC' : uf);
                    this.carregar(0, '');
                },
                error: () => this.carregar(0, '')
            });
    }

    /** Aplica os filtros atuais (botão Filtrar / Enter na busca). */
    protected aplicarFiltros(): void {
        this.currentPage.set(0);
        this.carregar(0, this.filtro());
    }

    /** Restaura os filtros ao estado inicial e recarrega. */
    protected limparFiltros(): void {
        this.filtro.set('');
        this.filtroTipo.set('');
        this.filtrarPorUf.set(true);
        const uf = this.ufUsuario();
        this.ufFiltro.set(uf === 'BR' ? 'AC' : uf);
        this.currentPage.set(0);
        this.carregar(0, '');
    }

    protected onPageChange(event: PaginatorState): void {
        if (event.rows !== undefined) this.pageSize.set(event.rows);
        const page = event.page ?? 0;
        this.currentPage.set(page);
        this.carregar(page, this.filtro());
    }

    protected onSortChange(event: { field: string; order: number }): void {
        const order = event.order as 1 | -1;
        // O p-table emite (onSort) ao receber [sortField]/[sortOrder] e a cada troca de [value];
        // sem este guard, cada carga de dados dispararia novo carregamento em loop.
        if (event.field === this.sortField() && order === this.sortOrder()) return;
        this.sortField.set(event.field);
        this.sortOrder.set(order);
        // Só atualiza os sinais de ordenação (como o filtro): a recarga ocorre no botão "Filtrar".
        // As pills do modo card escrevem nos mesmos sinais, por two-way binding.
    }

    // Os campos de UF apenas atualizam o estado; a aplicação ocorre no botão Filtrar
    protected onEstadoChange(uf: string): void {
        this.ufFiltro.set(uf);
    }

    protected onFiltrarPorUfChange(valor: boolean): void {
        this.filtrarPorUf.set(valor);
        if (valor && this.isUsuarioBR() && !this.ufFiltro()) {
            this.ufFiltro.set('AC');
        }
    }

    private carregar(page: number, q: string): void {
        this.carregando.set(true);
        const requisicao = ++this.requisicaoAtual;
        const dir = this.sortOrder() === 1 ? 'asc' : 'desc';
        const sgUf = this.filtrarPorUf() ? this.ufFiltro() : '';
        this.tipoDebitoService
            .pesquisar(q, sgUf, this.filtroTipo(), page, this.pageSize(), this.sortField(), dir)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    if (requisicao !== this.requisicaoAtual) return;
                    this.items.set(result.content);
                    this.totalRecords.set(result.totalElements);
                    this.carregando.set(false);
                },
                error: () => {
                    if (requisicao !== this.requisicaoAtual) return;
                    this.carregando.set(false);
                }
            });
    }

    protected tipoLabel(item: TipoDebitoManutencaoResponse): string {
        return item.in_anuidade === 'S' ? 'Anuidade' : item.in_anuidade === 'M' ? 'Multa Eleitoral' : 'Taxa';
    }

    protected tipoSeverity(item: TipoDebitoManutencaoResponse): 'success' | 'warn' | 'info' {
        return item.in_anuidade === 'S' ? 'success' : item.in_anuidade === 'M' ? 'warn' : 'info';
    }

    protected tipoBorderColor(item: TipoDebitoManutencaoResponse): string {
        return item.in_anuidade === 'S' ? 'var(--p-green-500)' : item.in_anuidade === 'M' ? 'var(--p-orange-500)' : 'var(--p-blue-500)';
    }

    abrirDetalhe(id: string): void {
        void this.router.navigate(['/configuracoes/arrecadacao/anuidades', id]);
    }

    abrirNovo(): void {
        void this.router.navigate(['/configuracoes/arrecadacao/anuidades/novo']);
    }
}
